import React from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import moment from 'moment';
import { PendingAntrag, FreigabeAuftrag, KontaktArt, PendingDokument } from '../../portal/ApiClient';
import { BoardCard, ART_LABEL } from './types';

// Anzeige-Labels der Kontakt-Arten (kontakt_log im Gateway).
export const KONTAKT_LABEL: Record<string, string> = {
  telefonat: '📞 Telefonat', versuch: '📵 Versuch',
  email: '📧 E-Mail', aufforderung: '📨 Aufforderung',
};

export interface BoardActions {
  zip: (a: PendingAntrag) => void;
  // Prüf-Ansicht: Datensatz + Dokumente/Nummern + Bilder + Änderungsprotokoll.
  pruefen: (a: PendingAntrag) => void;
  status: (a: PendingAntrag, status: string) => void;
  freigabe: (a: PendingAntrag, force: boolean) => void;
  bmimi: (a: PendingAntrag) => void;
  jetzt: (auf: FreigabeAuftrag) => void;
  storno: (auf: FreigabeAuftrag) => void;
  bestaetigen: (auf: FreigabeAuftrag) => void;
  // Erstantrag-Neuanlage: „übermitteln" (nur ins USP anlegen → p_id, kostenneutral)
  // und der kombinierte Weg (Ausweis-Check-Modal → anlegen + freigeben).
  uebernehmen: (a: PendingAntrag) => void;
  uebernehmenUndFreigeben: (a: PendingAntrag) => void;
  // Abgelaufen-Spalte: Verlängerungs-Aufforderung erneut mailen.
  aufforderung: (card: BoardCard) => void;
  // Abgelaufen-Spalte: Kontaktaufnahme protokollieren (Telefonat/Versuch/E-Mail).
  kontakt: (card: BoardCard, art: KontaktArt, note?: string) => void;
  // Nachgereichte Dokumente (Prüf-Queue): Sichtprüfung + Status setzen.
  dokAnsehen: (d: PendingDokument) => void;
  dokStatus: (d: PendingDokument, status: string) => void;
  // Echte USP-Übernahme in einem Klick (Modal mit Dokument-Metadaten).
  dokUebernehmen: (d: PendingDokument) => void;
}

const D = (d?: string | null, f = 'DD.MM.YYYY') => (d ? moment(d).format(f) : '–');

function ArtBadge({ card }: { card: BoardCard }) {
  if (card.einklick) return <Badge bg='success' className='me-1'>⚡ Ein-Klick</Badge>;
  if (!card.art) return null;
  return <Badge bg='light' text='dark' className='me-1'>{ART_LABEL[card.art] || card.art}</Badge>;
}

export default function KanbanCard({ card, actions, busyId }: { card: BoardCard; actions: BoardActions; busyId: number | null }) {
  const a = card.antrag;
  const auf = card.auftrag;
  const busy = a != null && busyId === a.id;
  const S = 'sm' as const;

  const buttons: React.ReactNode[] = [];
  if (a && (card.spalte === 'neu' || card.spalte === 'in_pruefung' || card.spalte === 'abgewiesen') && a.art !== 'bg_wechsel') {
    buttons.push(<Button key='pruefen' size={S} variant='primary' title='Datensatz ansehen: persönliche Daten, Dokumente samt Nummern, hochgeladene Bilder/PDFs und das Änderungsprotokoll.' onClick={() => actions.pruefen(a)}>🔍 Prüfen</Button>);
  }
  if (a) buttons.push(<Button key='zip' size={S} variant='outline-secondary' onClick={() => actions.zip(a)}>ZIP</Button>);

  // „Neu" UND „In Prüfung" tragen dieselben Freigabe-Aktionen -- ein Antrag mit
  // p_id (v.a. Verlängerungen) kann direkt freigegeben werden, ohne vorher
  // umständlich nach „In Prüfung" verschoben zu werden.
  if ((card.spalte === 'neu' || card.spalte === 'in_pruefung') && a) {
    if (a.art === 'bg_wechsel') {
      if (a.status === 'neu') {
        buttons.push(<Button key='bmimi' size={S} variant='primary' disabled={busy} onClick={() => actions.bmimi(a)}>📤 An BMIMI senden</Button>);
      } else {
        buttons.push(<Button key='zug' size={S} variant='outline-success' disabled={busy} onClick={() => actions.status(a, 'freigegeben')}>✔ Zugeordnet</Button>);
      }
    } else {
      if (card.spalte === 'neu') {
        buttons.push(<Button key='pruef' size={S} variant='outline-primary' disabled={busy} onClick={() => actions.status(a, 'in_pruefung')}>In Prüfung</Button>);
      }
      if (a.status === 'in_pruefung') {
        buttons.push(<Button key='usp' size={S} variant='outline-success' disabled={busy} onClick={() => actions.status(a, 'importiert')}>In USP erfasst</Button>);
      }
      if (a.status === 'in_pruefung' || a.status === 'importiert') {
        // Abschluss OHNE Freigabe: reines Dokument-Update bei gültiger ZÜP
        // (Doppelkosten-Schutz greift dann bewusst) — Fall Puelacher 08/2026.
        buttons.push(<Button key='erledigt' size={S} variant='outline-secondary' disabled={busy} title='Antrag ohne Freigabe abschließen — z.B. wenn nur ein neues Dokument übernommen wurde und die ZÜP noch gültig ist. Nimmt die Karte vom Board.' onClick={() => actions.status(a, 'erledigt')}>✔ Erledigt ohne Freigabe</Button>);
      }
      if (a.p_id) {
        buttons.push(<Button key='frei' size={S} variant='success' disabled={busy} title='Reiht die Freigabe ein — der Scheduler übermittelt sie automatisch im Behörden-Fenster (in der Betriebszeit sofort).' onClick={() => actions.freigabe(a, false)}>Freigeben</Button>);
        buttons.push(<Button key='sofort' size={S} variant='outline-warning' disabled={busy} title='Sofort ans Behörden-Portal, 40-Tage-Fenster übersteuern (nur 08:00–15:30). Löst Kosten sofort aus.' onClick={() => actions.freigabe(a, true)}>⚠️ Sofort</Button>);
      } else {
        // Erstantrag ohne p_id: zwei Wege. „In USP anlegen" übermittelt nur den
        // Datensatz (Neuanlage → p_id, kostenneutral, noch keine Freigabe).
        // „Anlegen + Freigeben" öffnet zuerst den Ausweis-Check (MRZ-Sicht-
        // prüfung), weil es direkt die kostenpflichtige Freigabe auslöst.
        buttons.push(<Button key='ueb' size={S} variant='outline-primary' disabled={busy} title='Legt die Person neu im USP an (Datensatz übermitteln → p_id). Kostenneutral, noch keine Freigabe.' onClick={() => actions.uebernehmen(a)}>📥 In USP anlegen</Button>);
        buttons.push(<Button key='uebfrei' size={S} variant='success' disabled={busy} title='Ausweis-Sichtprüfung, dann anlegen UND für die Behörde freigeben. Löst Kosten aus.' onClick={() => actions.uebernehmenUndFreigeben(a)}>🛂 Anlegen + Freigeben</Button>);
      }
    }
    buttons.push(<Button key='ablehn' size={S} variant='outline-danger' disabled={busy} onClick={() => actions.status(a, 'abgelehnt')}>Ablehnen</Button>);
  }

  if (card.spalte === 'freigabe_geplant' && auf) {
    if (auf.status === 'wartet_admin') {
      buttons.push(<Button key='best' size={S} variant='success' onClick={() => actions.bestaetigen(auf)}>Bestätigen</Button>);
    }
    if (auf.status === 'geplant') {
      buttons.push(<Button key='jetzt' size={S} variant='success' onClick={() => actions.jetzt(auf)}>⚡ Jetzt ausführen</Button>);
    }
    buttons.push(<Button key='storno' size={S} variant='outline-danger' onClick={() => actions.storno(auf)}>Stornieren</Button>);
  }

  const dok = card.dokument;
  if (dok) {
    buttons.push(
      <Button key='dok-an' size={S} variant='primary' title='Scan im neuen Tab öffnen (MRZ-Sichtprüfung).' onClick={() => actions.dokAnsehen(dok)}>🔍 Ansehen</Button>,
      <Button key='dok-ueb' size={S} variant='success' title='Nach der Sichtprüfung: Dokument mit einem Klick ins USP übernehmen (dokinfo + Datei + Gültigkeit) und Karte erledigen.' onClick={() => actions.dokUebernehmen(dok)}>⬆ Ins USP übernehmen</Button>,
      <Button key='dok-ok' size={S} variant='outline-secondary' title='Nur abhaken, OHNE Übernahme — z.B. wenn das Dokument anderweitig im USP gelandet ist.' onClick={() => actions.dokStatus(dok, 'erledigt')}>✓ Erledigt (ohne Übernahme)</Button>,
      <Button key='dok-ab' size={S} variant='outline-danger' onClick={() => actions.dokStatus(dok, 'abgelehnt')}>✗ Ablehnen</Button>,
    );
  }

  if (card.spalte === 'abgewiesen' && a) {
    buttons.push(<Button key='reopen' size={S} variant='outline-secondary' disabled={busy} onClick={() => actions.status(a, 'in_pruefung')}>Wieder öffnen</Button>);
  }

  if (card.spalte === 'abgelaufen' && !card.nv && !card.vorgang) {
    buttons.push(
      <Button key='auff' size={S} variant='outline-danger' disabled={!card.emailVorhanden}
        title={card.emailVorhanden ? 'Schickt die Verlängerungs-Aufforderung per Mail erneut.' : 'Keine E-Mail-Adresse bekannt — bitte anrufen.'}
        onClick={() => { if (window.confirm(`Aufforderung an ${card.name} senden?`)) actions.aufforderung(card); }}>
        📨 Aufforderung senden
      </Button>,
    );
  }
  if (card.spalte === 'abgelaufen' && !card.nv) {
    const logKontakt = (art: KontaktArt) => {
      const note = window.prompt('Optional: kurze Notiz zum Kontakt', '');
      if (note === null) return; // Abbrechen = nicht protokollieren
      actions.kontakt(card, art, note.trim() || undefined);
    };
    buttons.push(
      <Button key='k-tel' size={S} variant='outline-primary' title='Protokolliert: telefonisch erreicht.' onClick={() => logKontakt('telefonat')}>📞 Telefonat</Button>,
      <Button key='k-ver' size={S} variant='outline-secondary' title='Protokolliert: Anrufversuch, nicht erreicht.' onClick={() => logKontakt('versuch')}>📵 Versuch</Button>,
      <Button key='k-mail' size={S} variant='outline-primary' title='Protokolliert: E-Mail manuell geschickt (z.B. via Gmail).' onClick={() => logKontakt('email')}>📧 E-Mail</Button>,
    );
  }

  // Deep-Link ?mgd=<nr>: Karten dieses Mitglieds hervorheben + hinscrollen
  // (Fabian 04.11.2026 — Problem-Mails verlinken direkt auf den Fall).
  const fokusMgd = new URLSearchParams(window.location.search).get('mgd');
  const imFokus = !!fokusMgd && card.mgdNummer === fokusMgd;
  const kartenRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (imFokus && kartenRef.current) {
      kartenRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imFokus]);

  return (
    <Card ref={kartenRef} className={`mb-2 ${card.fehler ? 'border-danger' : ''} ${card.nv ? 'opacity-50' : ''} ${imFokus ? 'border-primary shadow' : ''}`}
      style={imFokus ? { borderWidth: 2, outline: '3px solid rgba(13,110,253,0.35)' } : undefined}>
      <Card.Body className='p-2'>
        <div className='fw-semibold' style={{ fontSize: '0.92rem' }}>
          {card.name} {card.mgdNummer && <span className='text-muted fw-normal'>({card.mgdNummer})</span>}
        </div>
        <div className='mb-1' style={{ fontSize: '0.8rem' }}>
          <ArtBadge card={card} />
          {card.pId && <span className='text-muted'>p_id {card.pId}</span>}
          {!card.pId && card.spalte !== 'radar' && card.quelle === 'antrag' && <Badge bg='warning' text='dark'>nicht verknüpft</Badge>}
        </div>

        {card.spalte === 'radar' && (
          <div style={{ fontSize: '0.82rem' }}>
            <div>läuft ab <strong>{D(card.datum)}</strong></div>
            {card.brauchtSr && (
              <div className='mt-1'>
                <Badge bg='dark' title='Auslands-Wohnsitz: braucht ein Strafregister/Führungszeugnis des Wohnsitzlands. Erinnerung schon ab T-60 (statt T-40).'>
                  🛂 Strafregister nötig{card.srLaender && card.srLaender.length > 0 ? ` (${card.srLaender.join(', ')})` : ''} · Vorlauf ab T-60
                </Badge>
              </div>
            )}
            <div className='mt-1'>
              <Badge bg={card.ampel === 'rot' ? 'danger' : card.ampel === 'gelb' ? 'warning' : 'secondary'}>{card.vorgang}</Badge>
            </div>
            {card.telefon && <div className='mt-1'><a href={`tel:${card.telefon}`}>📞 {card.telefon}</a></div>}
          </div>
        )}
        {card.spalte === 'behoerde' && (
          <div style={{ fontSize: '0.82rem' }}>
            <div>{card.behoerdenStatus} · freigegeben {D(card.datum)}</div>
            {card.resttage != null ? (
              <Badge bg={card.resttage > 14 ? 'success' : card.resttage > 7 ? 'warning' : 'danger'}>
                Behörde hat noch {card.resttage} T (bis {D(card.deadline, 'DD.MM.')})
              </Badge>
            ) : (
              <Badge bg='warning' text='dark' title='Die 28-Tage-Frist der Behörde beginnt erst, wenn der Flughafen den Antrag übernimmt (Status „Übernommen").'>
                ⏸ Frist startet erst mit Flughafen-Übernahme
              </Badge>
            )}
          </div>
        )}
        {card.spalte !== 'zuverlaessig' && card.spalte !== 'abgelaufen' && (
          <div className='text-muted' style={{ fontSize: '0.78rem' }}>
            {card.eFrist
              ? <>ZÜP bis <strong>{D(card.eFrist)}</strong></>
              : (card.art === 'erstantrag' || card.neuantrag) ? 'noch keine ZÜP (Erstantrag)' : 'ZÜP-Frist unbekannt'}
          </div>
        )}
        {card.spalte === 'zuverlaessig' && (
          <div style={{ fontSize: '0.82rem' }}>
            abgeschlossen {D(card.datum)}{card.eFrist && <> · zuverlässig bis {D(card.eFrist)}</>}
          </div>
        )}
        {card.spalte === 'freigabe_geplant' && (
          <div style={{ fontSize: '0.82rem' }}>
            {card.fehler ? <span className='text-danger'>Fehler: {card.note}</span>
              : <>freigegeben frühestens <strong>{D(card.datum)}</strong>{card.wartetAdmin && <Badge bg='warning' text='dark' className='ms-1'>wartet (alt)</Badge>}</>}
          </div>
        )}
        {card.quelle === 'dokument' && (
          <div style={{ fontSize: '0.82rem' }}>
            <Badge bg='info' text='dark'>📎 Dokument nachgereicht</Badge>
            <div className='mt-1'>{card.dokument?.filename}</div>
            {card.note && <div className='text-muted' style={{ fontSize: '0.78rem' }}>{card.note}</div>}
          </div>
        )}
        {(card.spalte === 'neu' || card.spalte === 'in_pruefung' || card.spalte === 'abgewiesen') && (
          <div className='text-muted' style={{ fontSize: '0.78rem' }}>{card.datumLabel} {D(card.datum, 'DD.MM. HH:mm')}</div>
        )}
        {card.spalte === 'abgelaufen' && (
          <div style={{ fontSize: '0.82rem' }}>
            <div>{card.neuantrag
              ? 'noch keine ZÜP (Erstantrag)'
              : card.datum ? <>abgelaufen am <strong>{D(card.datum)}</strong></> : 'keine gültige ZÜP (Status laut USP: unbekannt)'}</div>
            <div className='mt-1'>
              {card.nv
                ? <Badge bg='secondary'>gewollt abgelaufen (NV)</Badge>
                : card.neuantrag
                  ? <Badge bg='info' text='dark'>Neuantrag — Vorgang läuft</Badge>
                  : card.vorgang
                    ? <Badge bg='warning' text='dark'>abgelaufen — Vorgang läuft</Badge>
                    : <Badge bg='danger'>ungeplant abgelaufen</Badge>}
              {card.vorgang && <Badge bg='info' text='dark' className='ms-1'>{card.vorgang}</Badge>}
            </div>
            {card.letzterKontakt ? (
              <div className='text-muted mt-1' title={card.letzterKontakt.note || undefined}>
                zuletzt kontaktiert: {KONTAKT_LABEL[card.letzterKontakt.art] || card.letzterKontakt.art} {D(card.letzterKontakt.at)}
              </div>
            ) : card.letzteAufforderung && (
              <div className='text-muted mt-1'>zuletzt aufgefordert {D(card.letzteAufforderung)}</div>
            )}
            {card.telefon && <div className='mt-1'><a href={`tel:${card.telefon}`}>📞 {card.telefon}</a></div>}
          </div>
        )}

        {buttons.length > 0 && <div className='d-flex flex-wrap gap-1 mt-2'>{buttons}</div>}
      </Card.Body>
    </Card>
  );
}
